/**
 * Checks and balances on a project's money (spec §4.3, D6).
 *
 * Indhumathy's problem is that an overrun only becomes visible once the
 * invoices arrive and the sheet is updated after the fact. These surface it
 * at the three points where it can still be acted on:
 *
 *   1. when the roster commits more than was agreed  (before any invoice)
 *   2. when an invoice comes in above its agreed line price  (at review)
 *   3. when the invoices together pass the agreed cost  (as they land)
 *
 * Nothing here blocks anything. Vikas was explicit on 22 Sep that boosting
 * costs are decided near or after campaign end and a hard block would be
 * unreliable. These flag; a human decides.
 *
 * Pure.
 */

import { spendOf } from './commercials';

export const CHECK = {
  ROSTER_OVER_INTERNAL: 'roster_over_internal',
  INVOICED_OVER_INTERNAL: 'invoiced_over_internal',
  LINE_OVER_AGREED: 'line_over_agreed',
  LINE_NO_COST: 'line_no_cost',
  CLIENT_COST_GAP: 'client_cost_gap',
};

function amount(value) {
  const parsed = typeof value === 'number' ? value : parseFloat(String(value ?? '').trim());
  return Number.isFinite(parsed) ? parsed : 0;
}

const hasCost = (line) => amount(line && line.influencer_final_price) > 0;

/** What the roster has agreed to pay out, creators and vendors alike. */
export function committedOf(lines) {
  const list = Array.isArray(lines) ? lines : [];
  return list.reduce((total, line) => total + amount(line && line.influencer_final_price), 0);
}

/** What the lines together bill the client. */
function billedOf(lines) {
  const list = Array.isArray(lines) ? lines : [];
  return list.reduce((total, line) => total + amount(line && line.final_price), 0);
}

const money = (value) => `₹${Math.round(value).toLocaleString('en-IN')}`;

/**
 * `[{ id, severity, message, amount?, count?, names? }]`, warnings first.
 *
 * Checks that depend on an agreed internal cost stay silent until there is
 * one — a comparison against zero is noise, not a finding.
 */
export function projectChecks(input) {
  const { campaign, lines, invoices } = input || {};
  const project = campaign || {};
  const roster = Array.isArray(lines) ? lines : [];

  const internalCost = amount(project.internal_cost);
  const clientCost = amount(project.client_cost);
  const committed = committedOf(roster);
  const invoiced = spendOf(invoices);

  const warnings = [];
  const notes = [];

  if (internalCost > 0 && committed > internalCost) {
    warnings.push({
      id: CHECK.ROSTER_OVER_INTERNAL,
      severity: 'warn',
      amount: committed - internalCost,
      message: `Roster commits ${money(committed)} against ${money(internalCost)} internal cost — over by ${money(committed - internalCost)}.`,
    });
  }

  if (internalCost > 0 && invoiced > internalCost) {
    warnings.push({
      id: CHECK.INVOICED_OVER_INTERNAL,
      severity: 'warn',
      amount: invoiced - internalCost,
      message: `Invoiced ${money(invoiced)} against ${money(internalCost)} internal cost — over by ${money(invoiced - internalCost)}.`,
    });
  }

  // An invoice above the price that was locked with the creator. The lock is
  // what makes this mechanical instead of a memory test at review time.
  const overAgreed = roster.filter(
    (line) => hasCost(line) && amount(line.invoice_amount) > amount(line.influencer_final_price),
  );
  if (overAgreed.length > 0) {
    const names = overAgreed.map(
      (line) => line.influencer_name || line.vendor_name || 'Unnamed line',
    );
    warnings.push({
      id: CHECK.LINE_OVER_AGREED,
      severity: 'warn',
      names,
      count: names.length,
      message: `${names.length} invoice${names.length === 1 ? '' : 's'} above the agreed price: ${names.join(', ')}.`,
    });
  }

  if (clientCost > 0) {
    const billed = billedOf(roster);
    const gap = Math.abs(clientCost - billed);
    // Rounding on split lines is not a finding; a real gap is.
    if (gap >= 1) {
      warnings.push({
        id: CHECK.CLIENT_COST_GAP,
        severity: 'warn',
        amount: gap,
        message:
          billed < clientCost
            ? `Lines bill ${money(billed)} of the ${money(clientCost)} client cost — ${money(gap)} unaccounted for.`
            : `Lines bill ${money(billed)}, which is ${money(gap)} more than the ${money(clientCost)} client cost.`,
      });
    }
  }

  const unpriced = roster.filter((line) => !hasCost(line));
  if (unpriced.length > 0) {
    notes.push({
      id: CHECK.LINE_NO_COST,
      severity: 'info',
      count: unpriced.length,
      message: `${unpriced.length} line${unpriced.length === 1 ? '' : 's'} with no agreed cost yet — not counted above.`,
    });
  }

  return [...warnings, ...notes];
}
